import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { GenericSvg, SVGName } from "../../../../assets/svgs/GenericSVG";
import { Body } from "../../../../core/styles/stringStyles";
import { useStyles } from "../../../../core/styles/theme";
import { hexWithOpacity } from "../../../../core/utils/helpers";

export type SVGSelectionCanvas = {
  id: string;
  svg: SVGName;
  height: number;
  width: number;
  name: string;
  showOrientation?: boolean;
  selectedPoints?: { x: number; y: number }[];
};

type Props = {
  svgInfo: SVGSelectionCanvas;
  setSelectedPoints?: (id: string, points: { x: number; y: number }[]) => void;
};
export const SelectionCanvas = ({ svgInfo, setSelectedPoints }: Props) => {
  const theme = useStyles();
  const { t } = useTranslation();

  const handleSvgClick = (
    event: React.MouseEvent<SVGSVGElement, MouseEvent>
  ) => {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return;

    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;

    const { x, y } = point.matrixTransform(svg.getScreenCTM()!.inverse());

    // Check if click is on the SVG graphic or background
    const target = event.target as SVGElement;
    const isOnGraphic = target.tagName !== "svg" && target.tagName !== "DIV";
    if (!isOnGraphic) return;

    if (setSelectedPoints) {
      setSelectedPoints(svgInfo.id, [
        ...(svgInfo.selectedPoints ?? []),
        { x, y },
      ]);
    }
  };
  const handleRemovePoint = (index: number) => {
    if (svgInfo.selectedPoints && setSelectedPoints) {
      const newPoints = svgInfo.selectedPoints.filter((_, i) => i !== index);
      setSelectedPoints(svgInfo.id, newPoints);
    }
  };
  return (
    <Container>
      <svg
        width={svgInfo.width}
        height={svgInfo.height}
        style={{
          overflow: "visible",
        }}
        viewBox={`0 0 ${svgInfo.width} ${svgInfo.height}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow
              dx="0"
              dy="0"
              stdDeviation="4"
              floodColor="#000000"
              floodOpacity="0.4"
            />
          </filter>
          <radialGradient id="grad" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop
              offset="40%"
              style={{ stopColor: theme.colors.primary, stopOpacity: 0.6 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: theme.colors.primary, stopOpacity: 0 }}
            />
          </radialGradient>
          <radialGradient
            id="grad-hover"
            cx="50%"
            cy="50%"
            r="50%"
            fx="50%"
            fy="50%"
          >
            <stop
              offset="40%"
              style={{ stopColor: theme.colors.primary, stopOpacity: 0.8 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: theme.colors.primary, stopOpacity: 0 }}
            />
          </radialGradient>
        </defs>
        <GenericSvg
          name={svgInfo.svg}
          onClick={handleSvgClick}
          primaryColor={hexWithOpacity(theme.colors.medium, 0.8)}
        />
        {svgInfo.selectedPoints?.map((point, index) => (
          <g
            key={index}
            className="selection-point"
            onClick={() => handleRemovePoint(index)}
          >
            <circle cx={point.x} cy={point.y} r={30} fill={"url(#grad)"} />
          </g>
        ))}
      </svg>
      <OrientationContainer>
        {svgInfo.showOrientation && (
          <>
            <StyledBody>
              {t(svgInfo.id.includes("Back") ? "BODY_ORIENTATION_YOUR_LEFT" : "BODY_ORIENTATION_YOUR_RIGHT")}
            </StyledBody>
            <StyledBody>
              {t(svgInfo.id.includes("Back") ? "BODY_ORIENTATION_YOUR_RIGHT" : "BODY_ORIENTATION_YOUR_LEFT")}
            </StyledBody>
          </>
        )}
      </OrientationContainer>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  border-radius: 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  /* Default cursor for the container and SVG background */
  cursor: default;

  /* Pointer cursor only for SVG graphic elements (not the root svg element) */
  svg * {
    cursor: pointer;
  }

  /* Keep default cursor for the root svg element (background) */
  svg {
    cursor: default;
  }

  /* Hover effect for selection points */
  .selection-point {
    cursor: pointer;

    &:hover circle {
      fill: url(#grad-hover);
    }
  }
`;

const OrientationContainer = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: 0 5px;
  display: flex;
  justify-content: space-between;
`;

const StyledBody = styled(Body)`
  color: ${({ theme }) => theme.colors.medium};
`;
